import { Controller } from "@hotwired/stimulus"

// GSM-7 basic character set (standard SMS encoding)
const GSM_7_BASIC_CHARS = new Set([
  '@', '£', '$', '¥', 'è', 'é', 'ù', 'ì', 'ò', 'Ç', '\n', 'Ø', 'ø', '\r', 'Å', 'å',
  'Δ', '_', 'Φ', 'Γ', 'Λ', 'Ω', 'Π', 'Ψ', 'Σ', 'Θ', 'Ξ', '\x1B', 'Æ', 'æ', 'ß', 'É',
  ' ', '!', '"', '#', '¤', '%', '&', "'", '(', ')', '*', '+', ',', '-', '.', '/',
  '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', ':', ';', '<', '=', '>', '?',
  '¡', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O',
  'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', 'Ä', 'Ö', 'Ñ', 'Ü', '§',
  '¿', 'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o',
  'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z', 'ä', 'ö', 'ñ', 'ü', 'à'
])

// GSM-7 extended characters (each counts as 2 characters)
const GSM_7_EXTENDED_CHARS = new Set(['^', '{', '}', '\\', '[', ']', '~', '|', '€'])

export default class extends Controller {
  static targets = [
    "scheduleDateTime",
    "sendNowBtn",
    "scheduleBtn",
    "sendNowHidden",
    "body",
    "charCount",
    "segmentCount",
    "encodingBadge",
    "segmentWarning",
    "segmentWarningText",
    "noRecipientsWarning"
  ]

  static values = {
    prefix: { type: String, default: "" },
    suffix: { type: String, default: "" },
    segmentsRemaining: { type: String, default: "" },
    segmentLimit: { type: String, default: "" }
  }

  connect() {
    // Required: populates counters on load for the edit form with pre-populated body content.
    // submit cannot be handled via data-action on the form element in this context.
    this.element.addEventListener("submit", this.handleSubmit.bind(this))
    this.updateCounter()
  }

  hasRecipients() {
    const contactChecked = this.element.querySelectorAll('input[name="contact_ids[]"]:checked').length > 0
    const groupChecked = this.element.querySelectorAll('input[name="group_ids[]"]:checked').length > 0
    const selfChecked = this.element.querySelector('input[name="send_to_self"]')?.checked || false
    return contactChecked || groupChecked || selfChecked
  }

  handleSubmit(event) {
    if (!this.hasRecipients()) {
      event.preventDefault()
      this.noRecipientsWarningTarget.classList.remove("hidden")
      return
    }
    this.disableButtons()
  }

  // Build full message including prefix/suffix with newlines (matches send_sms_job.rb)
  getFullMessage() {
    const body = this.hasBodyTarget ? this.bodyTarget.value : ""
    const parts = []

    if (this.prefixValue) parts.push(this.prefixValue)
    parts.push(body)
    if (this.suffixValue) parts.push(this.suffixValue)

    return parts.join("\n")
  }

  // Check if message requires UCS-2 encoding (contains non-GSM-7 characters)
  isUCS2(message) {
    for (const char of message) {
      if (!GSM_7_BASIC_CHARS.has(char) && !GSM_7_EXTENDED_CHARS.has(char)) {
        return true
      }
    }
    return false
  }

  // Count GSM-7 character length (extended chars count as 2)
  countGSM7Length(message) {
    let length = 0
    for (const char of message) {
      length += GSM_7_EXTENDED_CHARS.has(char) ? 2 : 1
    }
    return length
  }

  // Calculate number of SMS segments
  calculateSegments(message) {
    const isUnicode = this.isUCS2(message)

    if (isUnicode) {
      const length = message.length
      if (length <= 70) {
        return { segments: length === 0 ? 0 : 1, encoding: 'UCS-2', charLength: length }
      }
      return { segments: Math.ceil(length / 67), encoding: 'UCS-2', charLength: length }
    } else {
      const length = this.countGSM7Length(message)
      if (length <= 160) {
        return { segments: length === 0 ? 0 : 1, encoding: 'GSM-7', charLength: length }
      }
      return { segments: Math.ceil(length / 153), encoding: 'GSM-7', charLength: length }
    }
  }

  updateCounter() {
    if (!this.hasCharCountTarget) return

    const fullMessage = this.getFullMessage()
    const { segments, encoding, charLength } = this.calculateSegments(fullMessage)

    this.charCountTarget.textContent = charLength
    this.segmentCountTarget.textContent = segments
    this.encodingBadgeTarget.textContent = encoding

    this.updateVisualFeedback(segments)
    this.updateSegmentWarning()
  }

  updateSegmentWarning() {
    if (!this.hasSegmentWarningTarget) return
    if (!this.segmentLimitValue || !this.segmentsRemainingValue) return

    const remaining = parseInt(this.segmentsRemainingValue, 10)
    const limit = parseInt(this.segmentLimitValue, 10)
    if (isNaN(remaining) || isNaN(limit) || limit <= 0) return

    // Past the limit, sends still go out and are billed as metered overage — nothing to warn about here.
    const isApproaching = remaining > 0 && (remaining / limit) <= 0.2

    if (!isApproaching) {
      this.segmentWarningTarget.classList.add("hidden")
      return
    }

    this.segmentWarningTarget.classList.remove("hidden")
    this.segmentWarningTarget.classList.add("bg-secondary-subtle", "border-secondary")
    this.segmentWarningTextTarget.classList.add("text-yellow-800")
    this.segmentWarningTextTarget.textContent =
      `Warning: You have ${remaining.toLocaleString()} segments remaining this month.`
  }

  updateVisualFeedback(segments) {
    const badge = this.encodingBadgeTarget

    // Reset classes
    badge.classList.remove(
      'bg-gray-100', 'text-gray-600',
      'bg-yellow-100', 'text-yellow-700',
      'bg-red-100', 'text-red-700'
    )

    if (segments <= 1) {
      badge.classList.add('bg-gray-100', 'text-gray-600')
    } else if (segments <= 3) {
      badge.classList.add('bg-yellow-100', 'text-yellow-700')
    } else {
      badge.classList.add('bg-red-100', 'text-red-700')
    }
  }

  disableButtons() {
    this.sendNowBtnTarget.disabled = true
    this.sendNowBtnTarget.value = "Sending..."
    this.scheduleBtnTarget.disabled = true
    this.scheduleBtnTarget.textContent = "Scheduling..."
  }

  updateButtons(event) {
    const isScheduled = event.target.value === "schedule"

    if (isScheduled) {
      this.scheduleDateTimeTarget.classList.remove("hidden")
      this.sendNowBtnTarget.classList.add("hidden")
      this.scheduleBtnTarget.classList.remove("hidden")
      this.sendNowHiddenTarget.value = "0"
    } else {
      this.scheduleDateTimeTarget.classList.add("hidden")
      this.sendNowBtnTarget.classList.remove("hidden")
      this.scheduleBtnTarget.classList.add("hidden")
      this.sendNowHiddenTarget.value = "1"
    }
  }

  submitSchedule(event) {
    event.preventDefault()
    if (!this.hasRecipients()) {
      this.noRecipientsWarningTarget.classList.remove("hidden")
      return
    }
    this.sendNowHiddenTarget.value = "0"
    this.disableButtons()
    this.element.submit()
  }
}
